import { useState, useEffect, useRef, useCallback } from 'react'
import { ChevronDown, Printer, Users, RefreshCw, Save, Check } from 'lucide-react'
import leiloesService from '../../services/leiloesService'
import mapasService from '../../services/mapasService'
import { useOrganizador } from '../../contexts/OrganizadorContext'

function Select({ label, value, onChange, children, disabled, className = '' }) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {label && <label className="text-xs text-gray-500 font-medium">{label}</label>}
      <div className="relative">
        <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
          className="w-full appearance-none border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400">
          {children}
        </select>
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>
    </div>
  )
}

export default function RelacaoVendedoresNominal() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id

  const [leiloes, setLeiloes] = useState([])
  const [leilaoId, setLeilaoId] = useState('')
  const [loadingL, setLoadingL] = useState(true)
  const [loading, setLoading] = useState(false)
  const [ordenacao, setOrdenacao] = useState('nome')
  const [vendedores, setVendedores] = useState([])
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [loadingPdf, setLoadingPdf] = useState(false)
  const [pdfUrl, setPdfUrl] = useState(null)
  const [showPdf, setShowPdf] = useState(false)
  const iframeRef = useRef(null)

  useEffect(() => {
    if (!orgId) return
    setLoadingL(true)
    leiloesService.getByOrganizador(orgId)
      .then(data => {
        const sorted = [...(data || [])].sort((a, b) => new Date(b.data_leilao) - new Date(a.data_leilao))
        setLeiloes(sorted)
        if (sorted.length) setLeilaoId(String((sorted.find(l => l.status === 'on') || sorted[0]).id))
      })
      .finally(() => setLoadingL(false))
  }, [orgId])

  const carregar = useCallback(() => {
    if (!orgId || !leilaoId) { setVendedores([]); return }
    setLoading(true)
    mapasService.getRelacaoVendedoresNominal(orgId, leilaoId)
      .then(data => setVendedores(data.vendedores || []))
      .catch(() => setVendedores([]))
      .finally(() => { setLoading(false); setDirty(false) })
  }, [orgId, leilaoId])

  useEffect(() => { carregar() }, [carregar])

  const leilaoAtual = leiloes.find(l => String(l.id) === leilaoId)

  const vendedoresOrdenados = [...vendedores].sort((a, b) => (
    ordenacao === 'qtd'
      ? (b.qtd_animais || 0) - (a.qtd_animais || 0)
      : a.nome.localeCompare(b.nome, 'pt-BR')
  ))

  const totalAnimais = vendedores.reduce((s, v) => s + (v.qtd_animais || 0), 0)

  const updateNominal = (vendedorId, valor) => {
    setVendedores(prev => prev.map(v => v.vendedor_id === vendedorId ? { ...v, nominal: valor } : v))
    setDirty(true)
  }

  const handleSalvar = async () => {
    setSaving(true)
    try {
      await mapasService.saveRelacaoVendedoresNominal(
        orgId, leilaoId,
        vendedores.map(v => ({ vendedor_id: v.vendedor_id, nominal: v.nominal || '' })),
      )
      setDirty(false)
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    } catch (err) {
      alert('Erro ao salvar: ' + (err.response?.data?.detail || err.message))
    } finally {
      setSaving(false)
    }
  }

  const handleImprimir = async () => {
    setLoadingPdf(true)
    try {
      if (dirty) await handleSalvar()
      const blob = await mapasService.getRelacaoVendedoresNominalPdf(orgId, leilaoId, ordenacao)
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }))
      setPdfUrl(url)
      setShowPdf(true)
    } catch (err) {
      alert('Erro: ' + (err.response?.statusText || err.message))
    } finally {
      setLoadingPdf(false)
    }
  }

  const closePdf = () => { setShowPdf(false); if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null) } }
  const printPdf = () => { if (iframeRef.current?.contentWindow) { iframeRef.current.contentWindow.focus(); iframeRef.current.contentWindow.print() } }

  if (!orgId) return <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">Selecione um organizador.</div>

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Users className="w-5 h-5 text-emerald-600" />
        <h1 className="text-lg font-semibold text-gray-800">Relação Vendedores (Nominal)</h1>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">
          <Select label="Leilão" value={leilaoId} onChange={v => setLeilaoId(v)} disabled={loadingL} className="flex-[2] min-w-[200px]">
            {loadingL ? <option>Carregando...</option> : leiloes.map(l => <option key={l.id} value={String(l.id)}>{l.nome}</option>)}
          </Select>
          <Select label="Ordenar por" value={ordenacao} onChange={v => setOrdenacao(v)} className="min-w-[160px]">
            <option value="nome">Alfabética</option>
            <option value="qtd">Qtd. Animais</option>
          </Select>
          <div className="flex items-end gap-2 ml-auto">
            {loading && <RefreshCw size={15} className="text-gray-400 animate-spin mb-2" />}
            <button onClick={handleSalvar} disabled={!vendedores.length || saving}
              className={`flex items-center gap-2 px-4 py-2 h-9 rounded-lg text-sm font-medium transition-colors ${
                saved ? 'bg-green-600 text-white' : 'bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white'
              }`}>
              {saved ? <><Check size={15} /> Salvo!</> : <><Save size={15} /> {saving ? 'Salvando...' : 'Salvar'}</>}
            </button>
            <button onClick={handleImprimir} disabled={!vendedores.length || loadingPdf}
              className="flex items-center gap-2 px-4 py-2 h-9 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors">
              <Printer size={15} /> {loadingPdf ? 'Gerando...' : 'Imprimir'}
            </button>
          </div>
        </div>
        {dirty && (
          <div className="mt-2 text-xs text-amber-600">Há alterações não salvas — clique em "Salvar" (ou "Imprimir", que salva automaticamente).</div>
        )}
      </div>

      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
      ) : !vendedores.length ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400 text-sm">Nenhum vendedor neste leilão</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm table-fixed">
              <colgroup>
                <col style={{ width: '30%' }} />
                <col style={{ width: '25%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '30%' }} />
              </colgroup>
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-2.5 font-semibold text-gray-600 text-xs">Vendedor</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-gray-600 text-xs">Fazenda</th>
                  <th className="text-center px-2 py-2.5 font-semibold text-gray-600 text-xs whitespace-nowrap">Qtd. Animais</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-emerald-700 text-xs">Nominal</th>
                </tr>
              </thead>
              <tbody>
                {vendedoresOrdenados.map(v => (
                  <tr key={v.vendedor_id} className="border-b border-gray-300 hover:bg-gray-50/60">
                    <td className="px-4 py-2 font-medium text-gray-800 truncate">{v.nome}</td>
                    <td className="px-4 py-2 text-gray-600 truncate">{v.fazenda || '—'}</td>
                    <td className="px-2 py-2 text-center text-gray-700">{v.qtd_animais}</td>
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        value={v.nominal || ''}
                        onChange={e => updateNominal(v.vendedor_id, e.target.value)}
                        placeholder="Digite aqui..."
                        className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </td>
                  </tr>
                ))}
                <tr className="bg-gray-50 font-semibold border-t-2 border-gray-300">
                  <td className="px-4 py-2.5">TOTAL</td>
                  <td className="px-4 py-2.5"></td>
                  <td className="px-2 py-2.5 text-center">{totalAnimais}</td>
                  <td className="px-4 py-2.5"></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showPdf && pdfUrl && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl flex flex-col" style={{ height: '90vh' }}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50 rounded-t-xl flex-shrink-0">
              <span className="font-semibold text-gray-800">Relação Vendedores (Nominal) — {leilaoAtual?.nome}</span>
              <div className="flex items-center gap-2">
                <button onClick={printPdf} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"><Printer size={15} /> Imprimir</button>
                <button onClick={closePdf} className="px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm">Fechar</button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden rounded-b-xl"><iframe ref={iframeRef} src={pdfUrl} title="Relação Vendedores (Nominal)" style={{ width: '100%', height: '100%', border: 'none' }} /></div>
          </div>
        </div>
      )}
    </div>
  )
}
